import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Button,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Dialog,
  DialogContent,
  Popover,
  Grid,
  DialogTitle,
  DialogActions,
  Paper,
} from '@mui/material'
import api from '@/services/api'
import toast from 'react-hot-toast'
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  FileDownload as FileDownloadIcon,
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  CalendarMonth as CalendarMonthIcon,
  ViewWeek as ViewWeekIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  TuneOutlined as TuneIcon,
  PersonOutline as PersonOutlineIcon,
  EventRepeat as EventRepeatIcon,
  GroupOutlined as GroupIcon,
  DirectionsCar as DriveIcon,
  Work as WorkIcon,
  WarningAmber as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PersonOutlined as PersonOutlinedIcon,
  StickyNote2Outlined as NotesIcon,
  FmdGoodOutlined as EvvIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  CloseOutlined as CloseOutlinedIcon,
} from '@mui/icons-material'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api'
import { useDebounce } from '@/hooks/useDebounce'
import {
  getDispatchProviders,
  getTodayVisitRoute,
  DispatchProvider,
  TodayVisitRouteResponse,
  exportProvidersCSV,
} from '@/services/agencyDispatchService'
import { useAgencyDirectory, getDirectoryOptionLabel } from '@/hooks/useAgencyDirectory'
import { GOOGLE_MAPS_LIBRARIES, getGoogleMapsApiKey } from '@/constants/googleMaps'
import AdvancedPagination from '@/components/pagination/AdvancedPagination'

// ─── Layout constants ────────────────────────────────────────────────────────
const TIMELINE_START_HOUR = 7
const TIMELINE_END_HOUR = 20
const HOUR_WIDTH = 140   // px per hour
const ROW_HEIGHT = 80    // px per provider row
const PROVIDER_COL_W = 220   // px for fixed left column

const HOURS = Array.from(
  { length: TIMELINE_END_HOUR - TIMELINE_START_HOUR },
  (_, i) => TIMELINE_START_HOUR + i
)
const TOTAL_W = HOURS.length * HOUR_WIDTH

// ─── Status palette ──────────────────────────────────────────────────────────


const S: Record<string, { bg: string; border: string; text: string; sub: string; dot: string; label: string }> = {
  scheduled: {
    bg: '#EBF8F1',
    border: '#52BF90',
    text: '#1B6B45',
    sub: '#3A9467',
    dot: '#52BF90',
    label: 'Scheduled',
  },
  clocked_in: {
    bg: '#FFFBEB',
    border: '#F59E0B',
    text: '#92400E',
    sub: '#B45309',
    dot: '#F59E0B',
    label: 'clocked-in',
  },
  clocked_out: {
    bg: '#F0FDF4',
    border: '#22C55E',
    text: '#14532D',
    sub: '#16A34A',
    dot: '#22C55E',
    label: 'clocked-out',
  },
  completed: {
    bg: '#E0F2FE',
    border: '#0EA5E9',
    text: '#075985',
    sub: '#0284C7',
    dot: '#0EA5E9',
    label: 'completed',
  },
  cancelled: {
    bg: '#FEF2F2',
    border: '#EF4444',
    text: '#991B1B',
    sub: '#DC2626',
    dot: '#EF4444',
    label: 'cancelled',
  },
};
const PRIMARY = '#7A6FA1'

const statusKey = (s: string) => (s || 'scheduled').toLowerCase().replace(/\s+/g, '_')
const cfg = (s: string) => S[statusKey(s)] ?? S.scheduled

// ─── Time helpers ─────────────────────────────────────────────────────────────
const toMin = (t: string) => {
  const m = t.trim().match(/^(\d+):(\d+)\s*(AM|PM)$/i)
  if (!m) return 0
  let h = +m[1]; const mm = +m[2]; const ap = m[3].toUpperCase()
  if (ap === 'PM' && h !== 12) h += 12
  if (ap === 'AM' && h === 12) h = 0
  return h * 60 + mm
}
const slotParts = (slot: string) => {
  const [a, b] = slot.split('-')
  return { start: toMin(a || ''), end: toMin(b || '') }
}
const toPx = (min: number) => ((min - TIMELINE_START_HOUR * 60) / 60) * HOUR_WIDTH
const fmtHr = (h: number) =>
  h === 12 ? '12:00 PM'
    : h < 12 ? `${String(h).padStart(2, '0')}:00 AM`
      : `${String(h - 12).padStart(2, '0')}:00 PM`
const initials = (f = '', l = '') => `${f[0] || ''}${l[0] || ''}`.toUpperCase()
const fmtDate = (d: string) => { try { return format(new Date(d), 'EEE, MMM d') } catch { return d } }

// ─── Session Block ───────────────────────────────────────────────────────────
const SessionBlock: React.FC<{ session: import('@/services/agencyDispatchService').DispatchSession }> = ({ session }) => {

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const { start } = slotParts(session.session_slot)
  const left = toPx(start) + 3
  const width = 130
  const c = cfg(session.status)
  const p = session.patient_id
  const name = typeof p === 'object' ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : 'Patient'
  const loc = typeof p === 'object' ? [p.city, p.state].filter(Boolean).join(', ') : ''
  const time = (session.session_slot.split('-')[0] || '').trim()

  // EVV data
  const evv = (session as any).evv
  const hasEvv = !!evv
  const clockIn = hasEvv && evv.clock_in ? format(new Date(evv.clock_in), 'hh:mm a') : null
  const clockOut = hasEvv && evv.clock_out ? format(new Date(evv.clock_out), 'hh:mm a') : null

  // Notes
  const notesArr: any[] = (session as any).notes || []
  const firstNote = notesArr[0] || null
  const noteContent = firstNote?.content
    ? firstNote.content.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim().slice(0, 120)
    : null

  // Formatted date
  const sessionDateFmt = session.session_date
    ? (() => { try { return format(new Date(session.session_date), 'EEEE, MMMM d, yyyy') } catch { return session.session_date } })()
    : '—'

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          position: 'absolute',
          left,
          top: 10,
          width,
          height: ROW_HEIGHT - 20,
          bgcolor: c.bg,
          border: `1.5px solid ${c.border}`,
          borderRadius: '8px',
          px: 1.25,
          py: 0.75,
          overflow: 'hidden',
          cursor: 'pointer',
          zIndex: 2,
          '&:hover': { boxShadow: `0 3px 10px ${c.border}44`, zIndex: 10 },
        }}
      >
        <Typography noWrap sx={{ color: c.text, fontWeight: 700, fontSize: '0.72rem', lineHeight: 1.4, display: 'block' }}>
          {name}&nbsp;&nbsp;{time}
        </Typography>
        {loc && (
          <Typography noWrap sx={{ color: c.sub, fontSize: '0.65rem', lineHeight: 1.3, display: 'block', mt: 0.15 }}>
            {loc}
          </Typography>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        disableRestoreFocus
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              borderRadius: '24px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
              border: '1px solid #EDE9FE',
              overflow: 'hidden',
              minWidth: 480,
              maxWidth: 540,
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 3, pt: 3, pb: 1.5,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#4F46E5', lineHeight: 1.2 }}>
              {session.specialization || 'Session'}
            </Typography>
          </Box>
          <Box display="flex" gap={0.5} alignItems="center">

            <IconButton size="small" sx={{ color: '#A89FC8' }} onClick={handleClose}>
              <CloseOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Body Layout: Side-by-side columns to make it more square / wide */}
        <Box sx={{ px: 3, pb: 3.5, pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 3.5 }}>
            {/* Left Column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
              {/* Patient */}
              <Box display="flex" alignItems="flex-start" gap={1.25}>
                <PersonOutlinedIcon sx={{ fontSize: 20, color: PRIMARY, mt: 0.15 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: PRIMARY, letterSpacing: '0.04em', mb: 0.25 }}>Patient</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#1F2937' }}>{name}</Typography>
                </Box>
              </Box>

              {/* Date & Time */}
              <Box display="flex" alignItems="flex-start" gap={1.25}>
                <AccessTimeIcon sx={{ fontSize: 20, color: PRIMARY, mt: 0.15 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: PRIMARY, letterSpacing: '0.04em', mb: 0.25 }}>Date &amp; Time</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1F2937' }}>{sessionDateFmt}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', mt: 0.2 }}>{session.session_slot}</Typography>
                </Box>
              </Box>

              {/* Session Type */}
              <Box display="flex" alignItems="flex-start" gap={1.25}>
                <PersonOutlinedIcon sx={{ fontSize: 20, color: PRIMARY, mt: 0.15 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: PRIMARY, letterSpacing: '0.04em', mb: 0.25 }}>Session Type</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1F2937', textTransform: 'capitalize' }}>
                    {session.session_type || '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Column: EVV status */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
              <Box>
                <Box display="flex" alignItems="center" gap={1.25} mb={0.75}>
                  <EvvIcon sx={{ fontSize: 20, color: PRIMARY }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: PRIMARY, letterSpacing: '0.04em' }}>EVV status</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 0.2 }}>
                  <Box display="flex" alignItems="center" gap={0.75} justifyContent="space-between">
                    <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                      Check-in: {clockIn || '—'}
                    </Typography>
                    {clockIn ? <CheckCircleIcon sx={{ fontSize: 18, color: '#22C55E' }} /> : null}
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.75} justifyContent="space-between">
                    <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                      Check-out: {clockOut || '—'}
                    </Typography>
                    {clockOut ? <CheckCircleIcon sx={{ fontSize: 18, color: '#22C55E' }} /> : null}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Notes full-width below the grid to look wider/square */}
          {noteContent && (
            <Box display="flex" alignItems="flex-start" gap={1.25} sx={{ borderTop: '1px solid #F3F4F6', pt: 2 }}>
              <NotesIcon sx={{ fontSize: 20, color: PRIMARY, mt: 0.15 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: PRIMARY, letterSpacing: '0.04em', mb: 0.25 }}>Notes</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#4B5563', lineHeight: 1.5 }}>
                  {noteContent}{noteContent.length >= 120 ? '…' : ''}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Bottom accent */}
        <Box sx={{ height: 6, background: 'linear-gradient(90deg, #7A6FA1, #4ECDC4)' }} />
      </Popover>
    </>
  )
}

// ─── View Route Modal ─────────────────────────────────────────────────────────
const mapStyle = { width: '100%', height: '100%' }

const ViewRouteModal: React.FC<{
  open: boolean
  onClose: () => void
  provider: DispatchProvider | null
  onRescheduleClick: (session: any) => void
  onCancelClick: (session: any) => void
  refreshTrigger?: number
}> = ({ open, onClose, provider, onRescheduleClick, onCancelClick, refreshTrigger }) => {
  const [data, setData] = useState<TodayVisitRouteResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: getGoogleMapsApiKey(), libraries: GOOGLE_MAPS_LIBRARIES })

  const fetchRouteData = useCallback(() => {
    if (!provider) return
    setLoading(true); setError(null); setData(null)
    getTodayVisitRoute(provider._id)
      .then(setData)
      .catch(e => setError(e?.response?.data?.message || 'Failed to load route'))
      .finally(() => setLoading(false))
  }, [provider])

  useEffect(() => {
    if (open && provider) {
      fetchRouteData()
    }
  }, [open, provider, fetchRouteData])

  // Refresh when reschedule succeeds (trigger from parent)
  useEffect(() => {
    if (!refreshTrigger) return
    fetchRouteData()
  }, [refreshTrigger, fetchRouteData])

  const sessions = data?.sessions || []
  const pts: google.maps.LatLngLiteral[] = []
  if (sessions.length) {
    const th = sessions[0].therapist_id
    if (th?.latitude && th?.longitude) pts.push({ lat: th.latitude, lng: th.longitude })
    sessions.forEach(s => { if (s.patient_id?.latitude && s.patient_id?.longitude) pts.push({ lat: s.patient_id.latitude, lng: s.patient_id.longitude }) })
  }
  const center = pts[0] || { lat: 34.0522, lng: -118.2437 }

  const handleMapLoad = (map: google.maps.Map) => {
    const infoWindow = new window.google.maps.InfoWindow()

    pts.forEach((pt, i) => {
      const color = i === 0 ? PRIMARY : '#EF4444'
      const svg =
        `<svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">` +
        `<path d="M20 0C9.50659 0 1 8.50659 1 19C1 29.4934 20 50 20 50C20 50 39 29.4934 39 19C39 8.50659 30.4934 0 20 0Z" fill="${color}" stroke="white" stroke-width="2"/>` +
        `<circle cx="20" cy="19" r="8" fill="white"/>` +
        `</svg>`

      const marker = new window.google.maps.Marker({
        position: pt,
        map,
        icon: {
          url: 'data:image/svg+xml;base64,' + btoa(svg),
          scaledSize: new window.google.maps.Size(40, 50),
          anchor: new window.google.maps.Point(20, 50),
          origin: new window.google.maps.Point(0, 0),
        },
      })

      // build tooltip content
      let content = ''
      if (i === 0) {
        const th = sessions[0]?.therapist_id
        content =
          `<div style="padding:10px 12px;min-width:150px;font-family:sans-serif">` +
          `<div style="font-weight:700;font-size:13px;color:#1F2937">${th?.firstName ?? ''} ${th?.lastName ?? ''}</div>` +
          `<div style="font-size:11px;color:${PRIMARY};font-weight:600;margin-top:3px">Provider</div>` +
          `</div>`
      } else {
        const s = sessions[i - 1]
        const p = s?.patient_id
        const c2 = cfg(s?.status ?? 'scheduled')
        content =
          `<div style="padding:10px 12px;min-width:190px;font-family:sans-serif">` +
          `<div style="font-weight:700;font-size:13px;color:#1F2937">${p?.firstName ?? ''} ${p?.lastName ?? ''}</div>` +
          `<div style="font-size:11px;color:#374151;margin-top:5px">🕐 ${s?.session_slot ?? ''}</div>` +
          `<div style="font-size:11px;color:#374151;margin-top:2px">⏱ ${s?.duration_mins ?? 0} mins</div>` +
          `<span style="display:inline-block;background:${c2.bg};color:${c2.text};font-size:10px;font-weight:700;padding:2px 8px;border-radius:4px;margin-top:6px;border:1px solid ${c2.border}">${c2.label}</span>` +
          `</div>`
      }

      marker.addListener('click', () => {
        infoWindow.setContent(content)
        infoWindow.open(map, marker)
      })
    })

    if (pts.length > 1) {
      new window.google.maps.Polyline({
        path: pts,
        strokeColor: PRIMARY,
        strokeOpacity: 0.9,
        strokeWeight: 3,
        map,
      })
    }

    if (pts.length >= 2) {
      const bounds = new window.google.maps.LatLngBounds()
      pts.forEach(p => bounds.extend(p))
      map.fitBounds(bounds, 60)
    }
  }

  const stats = [
    { label: 'TOTAL VISITS', value: String(data?.totalSessions ?? 0), icon: <GroupIcon sx={{ fontSize: 17 }} />, color: '#3B82F6' },
    { label: 'TOTAL DISTANCE', value: data?.totalDistance || '0 Miles', icon: <LocationOnIcon sx={{ fontSize: 17 }} />, color: '#9333EA' },
    { label: 'DRIVE TIME', value: data?.driveTime || '0 Hr', icon: <WarningIcon sx={{ fontSize: 17 }} />, color: '#F59E0B' },
    { label: 'TOTAL WORK', value: data?.totalWork || '0 Hr', icon: <AccessTimeIcon sx={{ fontSize: 17 }} />, color: '#10B981' },
  ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: '88vw', maxWidth: 1100, height: '85vh', borderRadius: '16px', overflow: 'hidden', m: 2 } }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}><CircularProgress sx={{ color: PRIMARY }} /></Box>
        ) : error ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1} p={3}><Alert severity="error">{error}</Alert></Box>
        ) : (
          <>
            {/* Left: Session List */}
            <Box sx={{ width: 360, flexShrink: 0, bgcolor: '#F5F3FF', overflowY: 'auto', p: 2.5, borderRight: '1px solid #E8E4F5' }}>
              <Typography variant="h6" fontWeight={700} mb={0.5} sx={{ color: '#1F2937' }}>Upcoming Sessions</Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5} sx={{ fontSize: '0.8rem' }}>
                {data?.totalSessions ?? 0} sessions scheduled for the upcoming week
              </Typography>
              {sessions.length === 0 ? (
                <Box textAlign="center" py={5}><EventRepeatIcon sx={{ fontSize: 48, color: '#C4B5FD', mb: 1 }} /><Typography color="text.secondary" variant="body2">No sessions for today</Typography></Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {sessions.map(s => {
                    const pt = s.patient_id
                    const c2 = cfg(s.status)
                    const lowerStatus = s.status?.toLowerCase()
                    const showReschedule = lowerStatus !== 'completed' && lowerStatus !== 'cancelled'

                    return (
                      <Box key={s._id} sx={{ bgcolor: '#fff', borderRadius: '12px', borderLeft: `4px solid ${PRIMARY}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                        <Box sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box display="flex" alignItems="center" gap={1.25}>
                              <Avatar src={pt?.profilePhotoUrl || undefined} sx={{ width: 40, height: 40, bgcolor: '#DDD6FE', color: PRIMARY, fontSize: 13, fontWeight: 700 }}>
                                {initials(pt?.firstName, pt?.lastName)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={400} sx={{ color: '#1F2937', lineHeight: 1.3 }}>Patient:</Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: '#1F2937', lineHeight: 1.3 }}>{pt?.firstName} {pt?.lastName}</Typography>

                              </Box>
                            </Box>
                            <Chip label={c2.label} size="small" sx={{ bgcolor: c2.bg, color: c2.text, fontWeight: 700, fontSize: '0.65rem', height: 20, px: 0.5, borderRadius: '6px' }} />
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
                            <AccessTimeIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                            <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>{fmtDate(s.session_date)}</Typography>
                            <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>•</Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>{s.session_slot}</Typography>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', mb: 1.5 }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.05em' }}>DURATION</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: '#1F2937' }}>{s.duration_mins} mins</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.05em' }}>SESSION ID</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: '#1F2937' }}>{s.session_ids}</Typography>
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                              <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.05em' }}>CONTRACT ID</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: '#1F2937' }}>{s.contract_ids}</Typography>
                            </Box>
                          </Box>
                          {pt?.email && <Box display="flex" alignItems="center" gap={0.75} mb={0.5}><EmailIcon sx={{ fontSize: 12, color: '#9CA3AF' }} /><Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>{pt.email}</Typography></Box>}
                          {pt?.phone && <Box display="flex" alignItems="center" gap={0.75}><PhoneIcon sx={{ fontSize: 12, color: '#9CA3AF' }} /><Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>{pt.phone}</Typography></Box>}
                          {showReschedule && (
                            <>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onCancelClick(s)}
                                sx={{
                                  mt: 1.5,
                                  width: '100%',
                                  textTransform: 'none',
                                  borderColor: '#EF4444',
                                  color: '#EF4444',
                                  fontWeight: 600,
                                  height: 32,
                                  borderRadius: '8px',
                                  '&:hover': {
                                    borderColor: '#DC2626',
                                    bgcolor: '#FFF1F1',
                                  }
                                }}
                              >
                                Cancel Session
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onRescheduleClick(s)}
                                sx={{
                                  mt: 1,
                                  width: '100%',
                                  textTransform: 'none',
                                  borderColor: PRIMARY,
                                  color: PRIMARY,
                                  fontWeight: 600,
                                  height: 32,
                                  borderRadius: '8px',
                                  '&:hover': {
                                    borderColor: '#6A5F8F',
                                    bgcolor: '#F3F0FF',
                                  }
                                }}
                              >
                                Reschedule
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Box>

            {/* Right: Stats + Map */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3, gap: 2.5, overflow: 'hidden', bgcolor: '#fff' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1F2937' }}>Today's Visit Route</Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.82rem' }}>Track provider travel flow and scheduled patient visits.</Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#9CA3AF' }}><CloseIcon /></IconButton>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1.5 }}>
                {stats.map(st => (
                  <Box key={st.label} sx={{ p: 2, border: '1px solid #F0ECFF', borderRadius: '12px', bgcolor: '#FAFAFA' }}>
                    <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                      <Box sx={{ color: st.color }}>{st.icon}</Box>
                      <Typography variant="caption" sx={{ color: st.color, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.06em' }}>{st.label}</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={800} sx={{ color: '#1F2937', lineHeight: 1 }}>{st.value}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ flex: 1, borderRadius: '14px', overflow: 'hidden', border: '1px solid #E5E7EB', minHeight: 240 }}>
                {isLoaded && data ? (
                  <GoogleMap
                    mapContainerStyle={mapStyle}
                    center={center}
                    zoom={14}
                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                    onLoad={handleMapLoad}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%"><CircularProgress size={28} sx={{ color: PRIMARY }} /></Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const AgencyDispatchPage: React.FC = () => {
  const [providers, setProviders] = useState<DispatchProvider[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalProviders, setTotal] = useState(0)

  const [search, setSearch] = useState('')
  const [selectedProvider, setSelectedProvider] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(new Date())
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [status, setStatus] = useState('')
  const [viewMode, setViewMode] = useState<'today' | 'week' | 'month'>('today')

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [routeProvider, setRouteProvider] = useState<DispatchProvider | null>(null)
  const [routeOpen, setRouteOpen] = useState(false)
  const [routeRefreshKey, setRouteRefreshKey] = useState(0)

  // ── Reschedule state (lifted to page level) ────────────────────────────────
  const [rescheduleOpen, setRescheduleOpen] = useState(false)
  const [selectedSessionForReschedule, setSelectedSessionForReschedule] = useState<any>(null)
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(new Date())
  const [rescheduleSlots, setRescheduleSlots] = useState<any[]>([])
  const [loadingRescheduleSlots, setLoadingRescheduleSlots] = useState(false)
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState<any>(null)
  const [isSubmittingReschedule, setIsSubmittingReschedule] = useState(false)
  const [rescheduleMonthOffset, setRescheduleMonthOffset] = useState(0)

  // ── Cancel state (lifted to page level) ───────────────────────────────────
  const [cancelOpen, setCancelOpen] = useState(false)
  const [selectedSessionForCancel, setSelectedSessionForCancel] = useState<any>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [isSubmittingCancel, setIsSubmittingCancel] = useState(false)

  const today = useMemo(() => new Date(), [])
  const pad = (n: number) => String(n).padStart(2, '0')
  const formatDateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const formatDateForAPI = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const formatDateDisplayLong = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const isPastDate = (d: Date) => {
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()) < todayOnly
  }

  const rescheduleMonthStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + rescheduleMonthOffset, 1),
    [today, rescheduleMonthOffset]
  )
  const rescheduleDaysInMonth = useMemo(
    () => new Date(rescheduleMonthStart.getFullYear(), rescheduleMonthStart.getMonth() + 1, 0).getDate(),
    [rescheduleMonthStart]
  )
  const rescheduleMonthDates = useMemo(() => {
    const arr: (Date | null)[] = []
    const firstDayOfWeek = rescheduleMonthStart.getDay()
    for (let i = 0; i < firstDayOfWeek; i++) arr.push(null)
    for (let i = 1; i <= rescheduleDaysInMonth; i++)
      arr.push(new Date(rescheduleMonthStart.getFullYear(), rescheduleMonthStart.getMonth(), i))
    return arr
  }, [rescheduleMonthStart, rescheduleDaysInMonth])

  useEffect(() => {
    if (!rescheduleDate || !rescheduleOpen || !selectedSessionForReschedule) {
      setRescheduleSlots([])
      return
    }
    const therapistId = typeof selectedSessionForReschedule.therapist_id === 'object'
      ? selectedSessionForReschedule.therapist_id?._id
      : selectedSessionForReschedule.therapist_id
    const serviceId = typeof selectedSessionForReschedule.service_id === 'object'
      ? selectedSessionForReschedule.service_id?._id
      : selectedSessionForReschedule.service_id
    if (!therapistId) return
    const formattedDate = formatDateForAPI(rescheduleDate)
      ; (async () => {
        setLoadingRescheduleSlots(true)
        try {
          const res = await api.get(
            `/therapist/schedule/available-slots?therapistId=${encodeURIComponent(therapistId)}&date=${formattedDate}&serviceId=${serviceId || ''}`
          )
          const serverData = res?.data?.data ?? []
          const normalized: any[] = Array.isArray(serverData)
            ? serverData.map((s: any) => {
              if (!s) return null
              const start = s.startTime ?? s.start_time ?? s.start
              const end = s.endTime ?? s.end_time ?? s.end
              if (!start || !end) return null
              return { _id: s._id, startTime: start, endTime: end }
            }).filter(Boolean)
            : []
          setRescheduleSlots(normalized)
        } catch { setRescheduleSlots([]) }
        finally { setLoadingRescheduleSlots(false) }
      })()
  }, [rescheduleDate, rescheduleOpen, selectedSessionForReschedule])

  const handleRescheduleClick = (session: any) => {
    setSelectedSessionForReschedule(session)
    setRescheduleDate(today)
    setSelectedRescheduleSlot(null)
    setRescheduleMonthOffset(0)
    setRescheduleOpen(true)
  }

  const handleRescheduleSubmit = async () => {
    if (!rescheduleDate || !selectedRescheduleSlot || !selectedSessionForReschedule) {
      toast.error('Please select a date and time slot')
      return
    }
    try {
      setIsSubmittingReschedule(true)
      const payload = {
        session_id: selectedSessionForReschedule._id,
        new_date: formatDateForAPI(rescheduleDate),
        new_slot: `${selectedRescheduleSlot.startTime}-${selectedRescheduleSlot.endTime}`,
      }
      await api.post('/contracts/session-reschedule', payload)
      toast.success('Session rescheduled successfully!')
      setRescheduleOpen(false)
      setSelectedRescheduleSlot(null)
      setSelectedSessionForReschedule(null)
      // Close route modal too — fresh data will load on next open
      setRouteOpen(false)
      setRouteProvider(null)
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reschedule session. Please try again.')
    } finally {
      setIsSubmittingReschedule(false)
    }
  }

  const handleCancelClick = (session: any) => {
    setSelectedSessionForCancel(session)
    setCancelReason('')
    setCancelOpen(true)
  }

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim() || !selectedSessionForCancel) {
      toast.error('Please provide a reason for cancellation')
      return
    }
    try {
      setIsSubmittingCancel(true)
      await api.post('/contracts/session-cancel', {
        session_id: selectedSessionForCancel._id,
        reason: cancelReason.trim(),
      })
      toast.success('Session cancelled successfully!')
      setCancelOpen(false)
      setCancelReason('')
      setSelectedSessionForCancel(null)
      setRouteOpen(false)
      setRouteProvider(null)
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to cancel session. Please try again.')
    } finally {
      setIsSubmittingCancel(false)
    }
  }

  const debouncedSearch = useDebounce(search, 500)
  const { providers: providerOpts } = useAgencyDirectory()
  const timelineRef = useRef<HTMLDivElement>(null)

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await getDispatchProviders({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        provider_ids: selectedProvider || undefined,
        session_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        // end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        status: status || undefined,
      })
      setProviders(res.providers || [])
      setTotal(res.totalProviders || 0)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load dispatch data')
      setProviders([])
    } finally { setLoading(false) }
  }, [page, rowsPerPage, debouncedSearch, selectedProvider, startDate, endDate, status])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    if (timelineRef.current && providers.length) {
      const now = new Date()
      timelineRef.current.scrollLeft = Math.max(0, toPx(now.getHours() * 60 + now.getMinutes()) - 200)
    }
  }, [providers])

  const shiftDate = (d: number) => {
    const nd = startDate ? new Date(startDate) : new Date()
    nd.setDate(nd.getDate() + d); setStartDate(nd)
  }
  const handleViewMode = (mode: 'today' | 'week' | 'month') => {
    const today = new Date()
    setViewMode(mode)
    setPage(0)
    if (mode === 'today') {
      setStartDate(today)
      setEndDate(today)
    } else if (mode === 'week') {
      setStartDate(startOfWeek(today, { weekStartsOn: 0 }))
      setEndDate(endOfWeek(today, { weekStartsOn: 0 }))
    } else {
      setStartDate(startOfMonth(today))
      setEndDate(endOfMonth(today))
    }
  }

  const clearFilters = () => {
    const today = new Date()
    setSearch(''); setSelectedProvider(''); setStartDate(today); setEndDate(today); setStatus(''); setViewMode('today'); setPage(0)
  }

  const handlePageChange = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setPage(0)
  }

  const handleExportCSV = async () => {
    try {
      setLoading(true)
      const params = {
        search: debouncedSearch || undefined,
        provider_ids: selectedProvider || undefined,
        session_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        status: status || undefined,
      }
      const blob = await exportProvidersCSV(params)
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `providers-dispatch-${format(new Date(), 'yyyy-MM-dd')}.csv`
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to export providers dispatch CSV')
    } finally {
      setLoading(false)
    }
  }

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes()
  const nowPx = toPx(nowMin)

  // Shared input style
  const inSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: '#F9FAFB',
      fontSize: '0.875rem',
      height: 40,
      '& fieldset': { borderColor: '#E5E7EB' },
      '&:hover fieldset': { borderColor: PRIMARY },
      '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
    },
  }
  const selSx = {
    borderRadius: '10px',
    bgcolor: '#F9FAFB',
    fontSize: '0.875rem',
    height: 40,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#F9FAFB', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#1F2937', fontSize: '1.5rem' }}>Dispatch Board</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.25, fontSize: '0.85rem' }}>Schedule and track provider visits in real-time.</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon sx={{ fontSize: 17 }} />}
          onClick={handleExportCSV}
          disabled={loading || providers.length === 0}
          sx={{ bgcolor: PRIMARY, color: '#fff', textTransform: 'none', borderRadius: '10px', fontWeight: 600, px: 2.5, py: 1, boxShadow: 'none', '&:hover': { bgcolor: '#6A5F8F', boxShadow: 'none' } }}
        >
          Export CSV
        </Button>
      </Box>

      {/* ── Filters ── */}
      <Card sx={{ mb: 2, borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', bgcolor: '#fff' }}>
        <CardContent sx={{ p: '16px 20px !important' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>

              {/* Search */}
              <Box sx={{ flex: '2 1 240px', minWidth: 190 }}>
                <Box display="flex" alignItems="center" gap={0.6} mb={0.85}>
                  <PersonOutlineIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem' }}>Search Provider/Patient</Typography>
                </Box>
                <TextField size="small" placeholder="Search by Provider/Patient Name..." value={search} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: '#9CA3AF' }} /></InputAdornment> }}
                  onChange={e => { setSearch(e.target.value); setPage(0) }}
                  sx={inSx}
                />
              </Box>

              {/* Provider */}
              <Box sx={{ flex: '1 1 170px', minWidth: 150 }}>
                <Box display="flex" alignItems="center" gap={0.6} mb={0.85}>
                  <PersonIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem' }}>Provider</Typography>
                </Box>
                <Select value={selectedProvider} onChange={e => { setSelectedProvider(e.target.value); setPage(0) }} displayEmpty size="small" fullWidth sx={selSx}>
                  <MenuItem value="">All Providers</MenuItem>
                  {providerOpts.map(p => <MenuItem key={p._id} value={p._id}>{getDirectoryOptionLabel(p)}</MenuItem>)}
                </Select>
              </Box>

              {/* Date range */}
              <Box sx={{ flex: '1.5 1 250px', minWidth: 230 }}>
                <Box display="flex" alignItems="center" gap={0.6} mb={0.85}>
                  <CalendarTodayIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem' }}>Date</Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <DatePicker value={startDate} onChange={v => setStartDate(v as Date | null)} format="MM/dd/yyyy"
                    slotProps={{ textField: { size: 'small', placeholder: 'Start Date', sx: { flex: 1, ...inSx } } }} />
                  {/* <DatePicker value={endDate} onChange={v => setEndDate(v as Date | null)} format="MM/dd/yyyy"
                    slotProps={{ textField: { size: 'small', placeholder: 'End Date', sx: { flex: 1, ...inSx } } }} /> */}
                </Box>
              </Box>

              {/* Status */}
              <Box sx={{ flex: '1 1 155px', minWidth: 140 }}>
                <Box display="flex" alignItems="center" gap={0.6} mb={0.85}>
                  <TuneIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.75rem' }}>Status</Typography>
                </Box>
                <Select value={status} onChange={e => { setStatus(e.target.value); setPage(0) }} displayEmpty size="small" fullWidth sx={selSx}>
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="clocked-in">Clocked In</MenuItem>
                  <MenuItem value="clocked-out">Clocked Out</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </Box>

              {/* Reset */}
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end' }}>
                <IconButton onClick={clearFilters}
                  sx={{ width: 40, height: 40, bgcolor: PRIMARY, color: '#fff', borderRadius: '10px', '&:hover': { bgcolor: '#6A5F8F' } }}>
                  <SyncIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Timeline Card ── */}
      <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', bgcolor: '#fff', overflow: 'hidden' }}>

        {/* Control bar: date nav + legend + view toggle */}
        <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #E5E7EB', bgcolor: '#fff', flexWrap: 'wrap' }}>
          {/* Date nav */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={() => shiftDate(-1)}
              sx={{ width: 28, height: 28, border: '1px solid #E5E7EB', borderRadius: '7px', color: '#6B7280', '&:hover': { borderColor: PRIMARY, color: PRIMARY } }}>
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#374151', px: 1, minWidth: 158, textAlign: 'center', fontSize: '0.85rem' }}>
              {startDate ? format(startDate, 'EEE, MMM d, yyyy') : format(new Date(), 'EEE, MMM d, yyyy')}
            </Typography>
            <IconButton size="small" onClick={() => shiftDate(1)}
              sx={{ width: 28, height: 28, border: '1px solid #E5E7EB', borderRadius: '7px', color: '#6B7280', '&:hover': { borderColor: PRIMARY, color: PRIMARY } }}>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Legend */}
          <Box display="flex" alignItems="center" gap={2} flex={1} flexWrap="wrap">
            {Object.values(S).map(c2 => (
              <Box key={c2.label} display="flex" alignItems="center" gap={0.65}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c2.dot, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.73rem' }}>{c2.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* View toggles */}

        </Box>

        {/* ── Grid ── */}
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={280}><CircularProgress sx={{ color: PRIMARY }} /></Box>
        ) : providers.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={280} gap={1}>
            <CalendarMonthIcon sx={{ fontSize: 52, color: '#DDD6FE' }} />
            <Typography variant="h6" sx={{ color: '#6B7280', fontWeight: 600, fontSize: '1rem' }}>No providers with sessions</Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.82rem' }}>Try adjusting filters or select a different date.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', overflow: 'hidden', bgcolor: '#fff' }}>

            {/* ── Fixed Left: Provider Column ── */}
            <Box sx={{ width: PROVIDER_COL_W, flexShrink: 0, borderRight: '1px solid #E5E7EB', bgcolor: '#fff', zIndex: 2 }}>
              {/* Column header */}
              <Box sx={{ height: 44, display: 'flex', alignItems: 'center', px: 2.5, borderBottom: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B7280', fontSize: '0.72rem', letterSpacing: '0.06em' }}>PROVIDER</Typography>
              </Box>
              {/* Provider rows */}
              {providers.map((p, i) => (
                <Box key={p._id} sx={{ height: ROW_HEIGHT, borderBottom: i < providers.length - 1 ? '1px solid #E5E7EB' : 'none', bgcolor: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center', px: 2.5, gap: 0.35 }}>
                  <Box display="flex" alignItems="center" gap={1.25}>
                    <Avatar src={p.profilePhotoUrl || undefined} sx={{ width: 34, height: 34, bgcolor: '#EDE9FE', color: PRIMARY, fontSize: 12, fontWeight: 700 }}>
                      {initials(p.firstName, p.lastName)}
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={700} noWrap sx={{ color: '#1F2937', fontSize: '0.82rem', lineHeight: 1.3 }}>
                        {p.firstName} {p.lastName}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ color: '#9CA3AF', fontSize: '0.69rem' }}>
                        {p.specialization?.map(s => s.name.match(/\(([^)]+)\)/)?.[1] || s.name.slice(0, 2).toUpperCase()).join(' • ')}
                      </Typography>
                    </Box>

                  </Box>

                  <Box display="flex" alignItems="center" gap={0.5} sx={{ ml: '46px', cursor: 'pointer', width: 'fit-content' }}
                    onClick={() => { setRouteProvider(p); setRouteOpen(true) }}>
                    <LocationOnIcon sx={{ fontSize: 12, color: PRIMARY }} />
                    <Typography variant="caption" sx={{ color: PRIMARY, fontSize: '0.7rem', fontWeight: 600, lineHeight: 1, '&:hover': { textDecoration: 'underline' } }}>
                      View Route
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>


            <Box ref={timelineRef} sx={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', width: TOTAL_W, height: 44, borderBottom: '1px solid #E5E7EB', bgcolor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 1 }}>
                {HOURS.map(h => (
                  <Box key={h} sx={{ width: HOUR_WIDTH, flexShrink: 0, borderRight: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', px: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600, fontSize: '0.7rem' }}>{fmtHr(h)}</Typography>
                  </Box>
                ))}
              </Box>


              <Box sx={{ position: 'relative', width: TOTAL_W, bgcolor: '#ffffff79' }}>
                {nowMin >= TIMELINE_START_HOUR * 60 && nowMin <= TIMELINE_END_HOUR * 60 && (
                  <Box sx={{
                    position: 'absolute', left: nowPx, top: 0, bottom: 0, width: 2, bgcolor: '#6366F1', zIndex: 5, pointerEvents: 'none',
                    '&::before': { content: '""', position: 'absolute', top: -1, left: -4, width: 10, height: 10, bgcolor: '#6366F1', borderRadius: '50%' },
                  }} />
                )}

                {providers.map((p, i) => (
                  <Box
                    key={p._id}
                    sx={{
                      height: ROW_HEIGHT,
                      position: 'relative',
                      bgcolor: '#fff',
                      borderBottom: '1px solid #E5E7EB',
                      borderRight: '1px solid #E5E7EB',
                    }}
                  >
                    {HOURS.map(h => (
                      <Box
                        key={h}
                        sx={{
                          position: 'absolute',
                          left: (h - TIMELINE_START_HOUR) * HOUR_WIDTH,
                          top: 0,
                          bottom: 0,
                          width: 0,
                          borderLeft: '1px solid #E5E7EB',
                          pointerEvents: 'none',
                        }}
                      />
                    ))}

                    {p.sessions.map(s => (
                      <SessionBlock key={s._id} session={s} />
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Footer */}
        {!loading && totalProviders > 0 && (
          <Box sx={{ px: 2.5, borderTop: '1px solid #E5E7EB', bgcolor: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.72rem', pl: 0.5 }}>
              {totalProviders} providers&nbsp;·&nbsp;{providers.reduce((a, p) => a + (p.sessions?.length || 0), 0)} sessions
            </Typography>
            <AdvancedPagination
              count={totalProviders}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        )}
      </Card>

      <ViewRouteModal
        open={routeOpen}
        onClose={() => { setRouteOpen(false); setRouteProvider(null) }}
        provider={routeProvider}
        onRescheduleClick={handleRescheduleClick}
        onCancelClick={handleCancelClick}
        refreshTrigger={routeRefreshKey}
      />

      {/* ── Reschedule Session Dialog (page-level, no blur conflict) ── */}
      <Dialog
        open={rescheduleOpen}
        onClose={() => !isSubmittingReschedule && setRescheduleOpen(false)}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>Reschedule Session</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            {selectedSessionForReschedule && (
              <Box sx={{ p: 2, mb: 2, backgroundColor: '#FFFBEB', borderRadius: 2, border: '1px solid #F59E0B' }}>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>Current Session</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    try { return format(new Date(selectedSessionForReschedule.session_date), 'MMMM dd, yyyy') + ' ' + selectedSessionForReschedule.session_slot }
                    catch { return selectedSessionForReschedule.session_date + ' ' + selectedSessionForReschedule.session_slot }
                  })()}
                </Typography>
              </Box>
            )}
            <Grid container spacing={3}>
              {/* Calendar */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton size="small" onClick={() => setRescheduleMonthOffset(m => m - 1)}><ChevronLeftIcon /></IconButton>
                      <Typography variant="body1" fontWeight={600}>
                        {rescheduleMonthStart.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
                      </Typography>
                      <IconButton size="small" onClick={() => setRescheduleMonthOffset(m => m + 1)}><ChevronRightIcon /></IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Select a day *</Typography>
                  </Box>
                  <Grid container spacing={0.5} sx={{ mb: 2 }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <Grid item xs={12 / 7} key={d}>
                        <Typography variant="caption" textAlign="center" display="block" color="text.secondary" fontWeight={600}>{d}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  <Grid container spacing={0.5}>
                    {rescheduleMonthDates.map((d, index) => {
                      if (d === null) return <Grid item xs={12 / 7} key={`empty-${index}`}><Box sx={{ aspectRatio: '1' }} /></Grid>
                      const key = formatDateKey(d)
                      const disabled = isPastDate(d)
                      const isActive = rescheduleDate && formatDateKey(rescheduleDate) === key
                      return (
                        <Grid item xs={12 / 7} key={key}>
                          <Button
                            onClick={() => !disabled && setRescheduleDate(d)}
                            disabled={disabled}
                            fullWidth
                            sx={{
                              aspectRatio: '1', minWidth: 0, p: 0, borderRadius: 1,
                              ...(disabled ? { bgcolor: 'grey.50', color: 'grey.300' }
                                : isActive ? { bgcolor: PRIMARY, color: 'white', '&:hover': { bgcolor: '#6A5F8F' } }
                                  : { bgcolor: 'white', color: 'text.primary', border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.50', borderColor: PRIMARY } })
                            }}
                          >{d.getDate()}</Button>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Paper>
              </Grid>
              {/* Time Slots */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="body2" fontWeight={600}>Select new slot time *</Typography>
                    {rescheduleDate && <Chip label={formatDateDisplayLong(rescheduleDate)} size="small" variant="outlined" />}
                  </Box>
                  {!rescheduleDate ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                      <Typography variant="body2" color="text.secondary" mb={1}>Select a date first</Typography>
                    </Box>
                  ) : loadingRescheduleSlots ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                      <CircularProgress size={24} sx={{ color: PRIMARY }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>Loading slots...</Typography>
                    </Box>
                  ) : rescheduleSlots.length === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                      <Typography variant="body2" color="text.secondary" mb={1}>No slots available</Typography>
                      <Typography variant="caption" color="text.secondary">Try selecting a different date</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {rescheduleSlots.map(slot => {
                        const label = `${slot.startTime} - ${slot.endTime}`
                        const isSelected = selectedRescheduleSlot &&
                          selectedRescheduleSlot.startTime === slot.startTime &&
                          selectedRescheduleSlot.endTime === slot.endTime
                        return (
                          <Button
                            key={slot._id ?? label}
                            onClick={() => setSelectedRescheduleSlot(slot)}
                            variant={isSelected ? 'contained' : 'outlined'}
                            fullWidth
                            sx={{
                              textTransform: 'none',
                              ...(isSelected
                                ? { bgcolor: PRIMARY, color: 'white', '&:hover': { bgcolor: '#6A5F8F' } }
                                : { borderColor: 'divider', '&:hover': { borderColor: PRIMARY, bgcolor: 'grey.50' } })
                            }}
                          >{label}</Button>
                        )
                      })}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRescheduleOpen(false)} disabled={isSubmittingReschedule}>Cancel</Button>
          <Button
            onClick={handleRescheduleSubmit}
            variant="contained"
            disabled={isSubmittingReschedule || !rescheduleDate || !selectedRescheduleSlot}
            sx={{ backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: '#6A5F8F' } }}
            startIcon={isSubmittingReschedule ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSubmittingReschedule ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Session Dialog (page-level) ── */}
      <Dialog
        open={cancelOpen}
        onClose={() => !isSubmittingCancel && setCancelOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>Cancel Session</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            {selectedSessionForCancel && (
              <Box sx={{ p: 2, mb: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={0.5}>Session Details</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    try { return format(new Date(selectedSessionForCancel.session_date), 'MMMM dd, yyyy') + ' ' + selectedSessionForCancel.session_slot }
                    catch { return selectedSessionForCancel.session_date + ' ' + selectedSessionForCancel.session_slot }
                  })()}
                </Typography>
              </Box>
            )}
            <TextField
              label="Reason for Cancellation *"
              multiline
              rows={4}
              fullWidth
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this session..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCancelOpen(false)} disabled={isSubmittingCancel}>Close</Button>
          <Button
            onClick={handleCancelSubmit}
            variant="contained"
            color="error"
            disabled={isSubmittingCancel || !cancelReason.trim()}
            startIcon={isSubmittingCancel ? <CircularProgress size={16} /> : null}
          >
            {isSubmittingCancel ? 'Cancelling...' : 'Cancel Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AgencyDispatchPage
